import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, DialogContent, DialogTitle, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ThemeStyle } from '../../../module/ThemeManager';
import { theme } from '../../../constants/theme';
import { EmailImageTab } from './EmailImageTab';

export interface IStep {
    displayTitle?: string;
    options?: string;
    instruction?: string;
}

export interface IEmailImageTabs {
    onChange?: (selected?: string) => void;
    step: IStep;
    themeStyle: ThemeStyle;
    maxSelection?: number;
    minSelection?: number;
    onSelect?: (option: string) => void;
    skipRefresh?: boolean;
    shouldShowButton?: boolean;
    onClickButton?: () => void;
    onClose?: () => void;
    disableButton?: boolean;
    maxSelectedDisableState?: boolean;
    initialValue?: string;
    buttonText?: string;
    emailImages?: string[];
}
const EmailImageTabs: React.FC<IEmailImageTabs> = ({
    step,
    onChange,
    themeStyle,
    skipRefresh,
    shouldShowButton,
    onClickButton,
    disableButton,
    initialValue,
    onClose,
    buttonText,
    emailImages,
}) => {
    const [selected, setSelected] = useState<string | undefined>();

    const handleSelection = (option: string) => {
        if (option === selected) {
            setSelected(undefined);
            onChange && onChange(undefined);
        } else {
            setSelected(option);
            onChange && onChange(option);
        }
    };

    useEffect(() => {
        if (initialValue) {
            setSelected(initialValue);
        }
    }, [step, skipRefresh, initialValue]);

    return (
        <AnimatePresence mode="wait">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                    opacity: 0,
                    transition: { ease: 'easeIn', duration: 1.0 },
                }}
                key={step.displayTitle}
                style={{ maxWidth: '550px' }}
                transition={{ duration: 3.5 }}>
                <DialogTitle
                    style={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].lightWhite,
                    }}
                    sx={{
                        fontSize: {
                            xs: 24,
                            sm: 32,
                        },
                    }}>
                    {step.displayTitle}
                </DialogTitle>
                <DialogContent style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', height: 500 }}>
                    {/* {step.options && */}
                    {emailImages &&
                        emailImages.map((emailImage, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                onClick={() => handleSelection(emailImage)}
                                transition={{ delay: index * 0.2, duration: 0.5 }}
                                style={{ margin: '5px', cursor: 'pointer' }}>
                                <EmailImageTab emailImage={emailImage} isActive={selected === emailImage} />
                            </motion.div>
                        ))}
                </DialogContent>
                {step.instruction && (
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].dropdown_text,
                            paddingLeft: '30px',
                        }}
                        sx={{
                            fontSize: {
                                xs: 12,
                                sm: 14,
                            },
                        }}>
                        {step.instruction}
                    </Typography>
                )}
                {shouldShowButton && (
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                        disabled={disableButton}
                        sx={{
                            color: theme[themeStyle].highlight,
                            border: `1px solid ${theme[themeStyle].highlight}`,
                            '&:hover': {
                                color: theme[themeStyle].highlight,
                                border: `1px solid ${theme[themeStyle].highlight}`,
                            },
                        }}
                        style={{
                            borderRadius: 50,
                            fontFamily: FONT_ENUM.MEDIUM_HEADER,
                            paddingRight: 60,
                            paddingLeft: 60,
                            marginLeft: 30,
                        }}>
                        {buttonText}
                    </Button>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export { EmailImageTabs };
